import { useState } from 'react'
import adminApi from '../../../services/adminApi'

const COHORT_PROGRAMS = [
  { slug: 'build3-cohort-7', label: 'Build3 Cohort 7' },
  { slug: 'energizers-march-cohort', label: 'Energizers March Cohort' },
  { slug: 'energizers-june-cohort', label: 'Energizers June Cohort' },
]

const normalizeEnrollment = (enrollment) => {
  const enrollmentData = enrollment.data || {}
  const user = enrollment.user || {}

  return {
    id: user.id ?? enrollment.id,
    name: user.name,
    email: user.email,
    phone: user.phone,
    gender: enrollmentData.gender,
    yogaExperience: enrollmentData.yogaExperience,
    energyLevel: enrollmentData.energyLevel,
    stressLevel: enrollmentData.stressLevel,
    emotionalState: enrollmentData.emotionalState,
    physicalState: enrollmentData.physicalState,
    wellnessGoal: enrollmentData.wellnessGoal,
    timingPreference: enrollmentData.timingPreference,
    customTiming: enrollmentData.customTiming,
    createdAt: enrollment.enrolledAt,
  }
}

const computeCohortStats = (signups) => {
  const entriesWithScores = signups.filter(signup => signup.energyLevel !== undefined && signup.energyLevel !== null)

  if (entriesWithScores.length === 0) {
    return {
      totalSignups: signups.length,
      averages: {
        energyLevel: 0,
        stressLevel: 0,
        emotionalState: 0,
        physicalState: 0,
      },
    }
  }

  const sums = entriesWithScores.reduce((acc, signup) => {
    acc.energyLevel += signup.energyLevel || 0
    acc.stressLevel += signup.stressLevel || 0
    acc.emotionalState += signup.emotionalState || 0
    acc.physicalState += signup.physicalState || 0
    return acc
  }, {
    energyLevel: 0,
    stressLevel: 0,
    emotionalState: 0,
    physicalState: 0,
  })

  const count = entriesWithScores.length

  return {
    totalSignups: signups.length,
    averages: {
      energyLevel: sums.energyLevel / count,
      stressLevel: sums.stressLevel / count,
      emotionalState: sums.emotionalState / count,
      physicalState: sums.physicalState / count,
    },
  }
}

export function useAdminData() {
  const [data, setData] = useState({
    stats: null,
    feedback: null,
    build3: null,
    wellness: null,
    cohort: null,
    cohortStats: null,
    cohortSessions: null,
    cohortCheckIns: null,
    cohortFeedback: null,
    midDayReset: null,
    spaces: null,
    facilitators: null,
    events: null,
    oneOnOneRequests: null,
    consultations: null,
    facConsultationListings: null,
    facConsultationInterests: null,
    certifications: null,
  })
  const [loadingTab, setLoadingTab] = useState(false)
  const [tabError, setTabError] = useState(null)
  const [exportingCSV, setExportingCSV] = useState(false)

  const fetchStats = async () => {
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/feedback-stats')
      setData(prev => ({ ...prev, stats: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load stats')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchFeedback = async () => {
    if (data.feedback) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/feedback')
      setData(prev => ({ ...prev, feedback: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load feedback')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchBuild3 = async () => {
    if (data.build3) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/build3-interest')
      setData(prev => ({ ...prev, build3: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load Build3 submissions')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchWellness = async () => {
    if (data.wellness) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/wellness-collaborative')
      setData(prev => ({ ...prev, wellness: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load Wellness Collaborative submissions')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchCohort = async () => {
    if (data.cohort) return
    try {
      setLoadingTab(true)
      setTabError(null)

      const allRequests = COHORT_PROGRAMS.flatMap(({ slug }) => [
        adminApi.get(`/api/admin/programs/${slug}/enrollments`),
        adminApi.get(`/api/programs/${slug}/sessions`),
        adminApi.get(`/api/admin/programs/${slug}/check-ins`),
        adminApi.get(`/api/admin/programs/${slug}/feedback`),
      ])

      const allResponses = await Promise.all(allRequests)

      const cohortBySlug = {}
      const cohortStatsBySlug = {}
      const sessionsBySlug = {}
      const checkInsBySlug = {}
      const feedbackBySlug = {}

      COHORT_PROGRAMS.forEach(({ slug }, index) => {
        const base = index * 4
        const signups = (allResponses[base].data.data || [])
          .filter(enrollment => enrollment.role === 'participant')
          .map(normalizeEnrollment)

        cohortBySlug[slug] = signups
        cohortStatsBySlug[slug] = computeCohortStats(signups)
        sessionsBySlug[slug] = allResponses[base + 1].data.data || []
        checkInsBySlug[slug] = allResponses[base + 2].data.data || []
        feedbackBySlug[slug] = allResponses[base + 3].data.data || []
      })

      setData(prev => ({
        ...prev,
        cohort: cohortBySlug,
        cohortStats: cohortStatsBySlug,
        cohortSessions: sessionsBySlug,
        cohortCheckIns: checkInsBySlug,
        cohortFeedback: feedbackBySlug,
      }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load Build3 Cohort data')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchMidDayReset = async () => {
    if (data.midDayReset) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/mid-day-reset-signups')
      setData(prev => ({ ...prev, midDayReset: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load Mid Day Reset signups')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchSpaces = async (forceRefresh = false) => {
    if (data.spaces && !forceRefresh) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const [spacesResponse, facilitatorsResponse] = await Promise.all([
        adminApi.get('/api/admin/spaces'),
        data.facilitators ? Promise.resolve({ data: { data: data.facilitators } }) : adminApi.get('/api/admin/facilitators')
      ])
      setData(prev => ({ 
        ...prev, 
        spaces: spacesResponse.data.data,
        facilitators: facilitatorsResponse.data.data
      }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load spaces')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchFacilitators = async () => {
    if (data.facilitators) return
    try {
      const response = await adminApi.get('/api/admin/facilitators')
      setData(prev => ({ ...prev, facilitators: response.data.data }))
    } catch (err) {
      console.error('Failed to load facilitators:', err)
    }
  }

  const fetchEvents = async (forceRefresh = false) => {
    if (data.events && !forceRefresh) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const [eventsResponse, spacesResponse, facilitatorsResponse] = await Promise.all([
        adminApi.get('/api/admin/events'),
        data.spaces ? Promise.resolve({ data: { data: data.spaces } }) : adminApi.get('/api/admin/spaces'),
        data.facilitators ? Promise.resolve({ data: { data: data.facilitators } }) : adminApi.get('/api/admin/facilitators')
      ])
      setData(prev => ({ 
        ...prev, 
        events: eventsResponse.data.data,
        spaces: spacesResponse.data.data,
        facilitators: facilitatorsResponse.data.data
      }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load events')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchOneOnOneRequests = async (forceRefresh = false) => {
    if (data.oneOnOneRequests && !forceRefresh) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/one-on-one-requests')
      setData(prev => ({ ...prev, oneOnOneRequests: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load 1-on-1 booking requests')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchConsultations = async (forceRefresh = false) => {
    if (data.consultations && !forceRefresh) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/corporate-consultations')
      setData(prev => ({ ...prev, consultations: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load consultation requests')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchFacConsultations = async (forceRefresh = false) => {
    if (data.facConsultationListings && !forceRefresh) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const [listingsRes, interestsRes, facilitatorsRes] = await Promise.all([
        adminApi.get('/api/admin/facilitator-consultations'),
        adminApi.get('/api/admin/facilitator-consultation-interests'),
        data.facilitators ? Promise.resolve({ data: { data: data.facilitators } }) : adminApi.get('/api/admin/facilitators'),
      ])
      setData(prev => ({
        ...prev,
        facConsultationListings: listingsRes.data.data,
        facConsultationInterests: interestsRes.data.data,
        facilitators: facilitatorsRes.data.data,
      }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load facilitator consultations')
    } finally {
      setLoadingTab(false)
    }
  }

  const fetchCertifications = async (forceRefresh = false) => {
    if (data.certifications && !forceRefresh) return
    try {
      setLoadingTab(true)
      setTabError(null)
      const response = await adminApi.get('/api/admin/certifications')
      setData(prev => ({ ...prev, certifications: response.data.data }))
    } catch (err) {
      setTabError(err.response?.data?.message || 'Failed to load certifications')
    } finally {
      setLoadingTab(false)
    }
  }

  const loadTabData = (tab) => {
    setTabError(null)
    
    if (tab === 'stats' && !data.stats) {
      fetchStats()
    } else if (tab === 'feedback' && !data.feedback) {
      fetchFeedback()
    } else if (tab === 'build3' && !data.build3) {
      fetchBuild3()
    } else if (tab === 'wellness' && !data.wellness) {
      fetchWellness()
    } else if (tab === 'cohort' && !data.cohort) {
      fetchCohort()
    } else if (tab === 'midDayReset' && !data.midDayReset) {
      fetchMidDayReset()
    } else if (tab === 'spaces' && !data.spaces) {
      fetchSpaces()
    } else if (tab === 'events' && !data.events) {
      fetchEvents()
    } else if (tab === 'oneOnOne' && !data.oneOnOneRequests) {
      fetchOneOnOneRequests()
    } else if (tab === 'consultations' && !data.consultations) {
      fetchConsultations()
    } else if (tab === 'facConsultations' && !data.facConsultationListings) {
      fetchFacConsultations()
    } else if (tab === 'certifications' && !data.certifications) {
      fetchCertifications()
    }
  }

  const handleExportCSV = async (programSlug, defaultFilename) => {
    try {
      setExportingCSV(true)
      const response = await adminApi.get(`/api/admin/programs/${programSlug}/export-csv`, {
        responseType: 'blob'
      })
      
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      const contentDisposition = response.headers['content-disposition']
      let filename = defaultFilename
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match) filename = match[1]
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error exporting CSV:', err)
      setTabError(err.response?.data?.message || 'Failed to export CSV')
    } finally {
      setExportingCSV(false)
    }
  }

  const ensureSpacesAndFacilitators = async () => {
    if (!data.spaces || !data.facilitators) {
      try {
        const [spacesRes, facsRes] = await Promise.all([
          data.spaces ? Promise.resolve({ data: { data: data.spaces } }) : adminApi.get('/api/admin/spaces'),
          data.facilitators ? Promise.resolve({ data: { data: data.facilitators } }) : adminApi.get('/api/admin/facilitators')
        ])
        setData(prev => ({
          ...prev,
          spaces: spacesRes.data.data,
          facilitators: facsRes.data.data
        }))
      } catch (err) {
        console.error('Failed to load spaces/facilitators:', err)
      }
    }
  }

  const ensureFacilitators = async () => {
    if (!data.facilitators) {
      await fetchFacilitators()
    }
  }

  return {
    data,
    loadingTab,
    tabError,
    exportingCSV,
    loadTabData,
    fetchStats,
    fetchSpaces,
    fetchEvents,
    fetchOneOnOneRequests,
    fetchConsultations,
    fetchFacConsultations,
    fetchCertifications,
    handleExportCSV,
    ensureSpacesAndFacilitators,
    ensureFacilitators,
  }
}
